import { useCallback, useEffect, useState } from "react";

import { useUpdateAddressBookItem } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import type {
	AddressBookItemSummary,
	AddressBookUpdateBodyV2,
} from "~/shared/schemas/address-book-schema.ts";

type UseAddressDeletionProps = {
	setData: React.Dispatch<React.SetStateAction<AddressBookItemSummary[]>>;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	showModal: boolean;
	stopType: 1 | 2;
};

export function useAddressDeletion({
	setData,
	setIsLoading,
	showModal,
	stopType,
}: UseAddressDeletionProps) {
	const updateAddressBookItemMutation = useUpdateAddressBookItem();
	const { showSuccess, showError } = useNotifications();

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [itemToDelete, setItemToDelete] =
		useState<AddressBookItemSummary | null>(null);

	useEffect(() => {
		if (!showModal) {
			setShowConfirmation(false);
			setItemToDelete(null);
		}
	}, [showModal]);

	const handleRemoveFromList = useCallback(
		(dataItem: AddressBookItemSummary) => {
			setItemToDelete(dataItem);
			setShowConfirmation(true);
		},
		[],
	);

	const handleConfirmDelete = useCallback(() => {
		if (!itemToDelete) return;

		const pendingDeletion = itemToDelete;
		const resolveDisplayName = () => {
			const trimmedName = pendingDeletion.companyName?.trim();
			return trimmedName && trimmedName.length > 0 ? trimmedName : "Address";
		};
		const stopTypeLabel = stopType === 1 ? "pickup" : "delivery";

		setShowConfirmation(false);
		setIsLoading(true);

		// Filter out the detail with the matching stopType
		const remainingDetails = pendingDeletion.details.filter(
			(detail) => detail.stopType !== stopType,
		);

		// Update the item with the remaining details (remove only the specific stopType detail)
		const updateData: AddressBookUpdateBodyV2 = {
			addressBookItemId: pendingDeletion.addressBookItemId,
			customerId: pendingDeletion.customerId,
			companyName: pendingDeletion.companyName ?? undefined,
			address1: pendingDeletion.address1,
			address2: pendingDeletion.address2 ?? undefined,
			postalCode: pendingDeletion.postalCode ?? undefined,
			city: pendingDeletion.city ?? undefined,
			stateProvince: pendingDeletion.stateProvince ?? undefined,
			country: pendingDeletion.country,
			isValidated: pendingDeletion.isValidated ?? undefined,
			legacyAddressId: pendingDeletion.legacyAddressId ?? undefined,
			details: remainingDetails.map((detail) => ({
				contactDetailId: detail.contactDetailId,
				addressBookItemId: detail.addressBookItemId,
				contactName: detail.contactName ?? undefined,
				contactPhone: detail.contactPhone ?? undefined,
				contactExtension: detail.contactExtension,
				hoursOpen: detail.hoursOpen ?? undefined,
				hoursClose: detail.hoursClose ?? undefined,
				stopType: detail.stopType,
				mode: detail.mode,
				timeZone: detail.timeZone ?? undefined,
				specialInstructions: detail.specialInstructions ?? undefined,
				accessorials: detail.accessorials ?? undefined,
			})),
		};

		updateAddressBookItemMutation.mutate(updateData, {
			onSuccess: () => {
				// Remove from the local data since it no longer has the current stopType detail
				setData((prev) =>
					prev.filter(
						(item) =>
							item.addressBookItemId !== pendingDeletion.addressBookItemId,
					),
				);
				setIsLoading(false);
				showSuccess(
					`${resolveDisplayName()} ${stopTypeLabel} details removed from the address book.`,
				);
				setItemToDelete(null);
			},
			onError: (error) => {
				setIsLoading(false);
				showError(
					`Unable to remove ${stopTypeLabel} details from ${resolveDisplayName()}. Please try again shortly.`,
				);
				setItemToDelete(null);
				console.error("Failed to update address book item:", error);
			},
		});
	}, [
		itemToDelete,
		stopType,
		updateAddressBookItemMutation,
		setData,
		setIsLoading,
		showError,
		showSuccess,
	]);

	const handleCancelDelete = useCallback(() => {
		setShowConfirmation(false);
		setItemToDelete(null);
	}, []);

	return {
		showConfirmation,
		itemToDelete,
		handleRemoveFromList,
		handleConfirmDelete,
		handleCancelDelete,
	};
}
